// src/pages/driver/DriverDashboard.jsx
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp, arrayUnion } from "firebase/firestore";
import { db } from "../../services/firebase";
import DriverMap from "./DriverMap";
import DriverJobList from "./DriverJobList";
import useGPSListener from "./useGPSListener";
import useDriverAssignment from "./useDriverAssignment";
import PostTripModal from "../../components/PostTripModal";

const DRIVER_DASHBOARD_AUTO_REFRESH_MS = 15 * 60 * 1000;
const DRIVER_DASHBOARD_REFRESH_TIME_ZONE = "Australia/Melbourne";

const getDriverDashboardRefreshDate = () =>
  new Date().toLocaleDateString("en-CA", {
    timeZone: DRIVER_DASHBOARD_REFRESH_TIME_ZONE,
  });

export default function DriverDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = getAuth().currentUser;

  const [date, setDate] = useState(() =>
    searchParams.get("date") ||
    new Date().toLocaleDateString("en-CA", {
      timeZone: "Australia/Melbourne",
    }),
  );
  const [showMapMobile, setShowMapMobile] = useState(false);
  const [postTripSectionOpen, setPostTripSectionOpen] = useState(false);
  const [activePostTripCtx, setActivePostTripCtx] = useState(null);

  useEffect(() => {
    if (!user?.uid || typeof window === "undefined") return undefined;

    const today = getDriverDashboardRefreshDate();
    const refreshKey = `driverDashboardDailyRefresh:${user.uid}`;

    try {
      if (window.localStorage.getItem(refreshKey) !== today) {
        window.localStorage.setItem(refreshKey, today);
        const reloadTimer = window.setTimeout(() => {
          window.location.reload();
        }, 1500);

        return () => window.clearTimeout(reloadTimer);
      }
    } catch (err) {
      console.warn("Unable to store driver dashboard refresh state:", err);
    }

    return undefined;
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid || typeof window === "undefined") return undefined;

    const intervalId = window.setInterval(() => {
      window.location.reload();
    }, DRIVER_DASHBOARD_AUTO_REFRESH_MS);

    return () => window.clearInterval(intervalId);
  }, [user?.uid]);

  useEffect(() => {
    const urlDate = searchParams.get("date");
    if (urlDate && urlDate !== date) {
      setDate(urlDate);
    }
  }, [searchParams, date]);

  // Keep existing GPS hook
  useGPSListener(user);

  // Live location writer -> Firestore locations/{driverId}
  useEffect(() => {
    if (!user) return;
    if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
      console.warn("Geolocation not available");
      return;
    }

    const driverId = user.uid;
    const locRef = doc(db, "locations", driverId);

    const watchId = navigator.geolocation.watchPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;

        try {
          await setDoc(
            locRef,
            {
              lat: latitude,
              lng: longitude,
              timestamp: serverTimestamp(),
              path: arrayUnion({
                lat: latitude,
                lng: longitude,
                timestamp: Date.now(),
              }),
            },
            { merge: true },
          );
        } catch (err) {
          console.error("Failed to update driver location:", err);
        }
      },
      (error) => {
        console.error("Geolocation error:", error);
      },
      {
        enableHighAccuracy: true,
        maximumAge: 10000,
        timeout: 20000,
      },
    );

    return () => {
      navigator.geolocation.clearWatch(watchId);
    };
  }, [user]);

  const {
    assignment,
    loading,
    onBreak,
    handleBreakToggle,
    handleTimestamp,
    refresh,
    breakHistory = [],
    assignmentDocId,
    runDocMap,
  } = useDriverAssignment(date);

  const runs = useMemo(() => assignment?.runs || {}, [assignment]);
  const runMeta = useMemo(() => assignment?.runMeta || {}, [assignment]);

  // Flatten addresses safely (only arrays)
  const allAddresses = useMemo(() => {
    if (!assignment || !runs) return [];
    return Object.values(runs).filter(Array.isArray).flat().filter(Boolean);
  }, [assignment, runs]);

  const totalJobsCount = useMemo(() => {
    return Object.values(runs).filter(Array.isArray).flat().filter(Boolean)
      .length;
  }, [runs]);

  // Build a small summary of Run -> Company (from runMeta, or from job items fallback)
  const runCompanySummary = useMemo(() => {
    if (!assignment) return [];

    const labels = Object.keys(runs || {});
    const summary = labels.map((label) => {
      const meta = runMeta?.[label] || null;

      // fallback: try first job in run
      const firstJob = Array.isArray(runs[label]) ? runs[label][0] : null;

      const companyName =
        meta?.companyName ||
        firstJob?.companyName ||
        (meta?.companyId && meta.companyId !== "mixed" ? meta.companyId : "") ||
        "";

      const uiVariant = meta?.uiVariant || firstJob?.uiVariant || "";

      const count = Array.isArray(runs[label])
        ? runs[label].filter(Boolean).length
        : 0;

      return {
        label,
        companyName,
        uiVariant,
        count,
      };
    });

    // Only keep runs that actually have jobs
    return summary.filter((s) => s.count > 0);
  }, [assignment, runs, runMeta]);

  const safeRunKey = (value = "") =>
    String(value || "")
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^a-zA-Z0-9_-]/g, "");

  const getRunRequirement = (label) => {
    const meta = runMeta?.[label] || null;
    const firstJob = Array.isArray(runs[label]) ? runs[label][0] : null;
    const companyId = meta?.companyId || firstJob?.companyId || "";
    const companyName = meta?.companyName || firstJob?.companyName || companyId || "";
    const uiVariant = String(meta?.uiVariant || firstJob?.uiVariant || "").toLowerCase();
    const isGreenspace = uiVariant === "greenspace";
    const vehicleId = meta?.vehicleId || firstJob?.vehicleId || "";
    const vehicleRego = meta?.vehicleRego || meta?.rego || meta?.truckRego || firstJob?.vehicleRego || firstJob?.rego || firstJob?.truckRego || "";
    const vehicleLoadType =
      meta?.vehicleLoadType ||
      meta?.loadType ||
      meta?.unitType ||
      meta?.truckType ||
      firstJob?.vehicleLoadType ||
      firstJob?.loadType ||
      firstJob?.unitType ||
      firstJob?.truckType ||
      "";
    const vehicleLabel =
      meta?.vehicleLabel ||
      meta?.truckLabel ||
      firstJob?.vehicleLabel ||
      firstJob?.truckLabel ||
      [vehicleRego, vehicleLoadType].filter(Boolean).join(" • ");
    const hasVehicle = !!(vehicleId || vehicleRego || vehicleLoadType || vehicleLabel);
    const runDocId = `${user.uid}_${date}_${safeRunKey(label)}`;
    const prestartDocId = (isGreenspace || hasVehicle)
      ? `${user.uid}_${date}_${safeRunKey(label)}`
      : companyId
        ? `${companyId}_${user.uid}_${date}`
        : `${user.uid}_${date}`;
    return {
      isGreenspace,
      companyId,
      companyName,
      prestartDocId,
      postTripDocId: hasVehicle || isGreenspace ? runDocId : prestartDocId,
      label,
      vehicleId,
      vehicleRego,
      vehicleLoadType,
      vehicleLabel,
      hasVehicle,
    };
  };

  const runPostTripRequirements = useMemo(
    () =>
      Object.keys(runs || {})
        .map((label) => {
          const list = Array.isArray(runs[label]) ? runs[label].filter(Boolean) : [];
          return { ...getRunRequirement(label), count: list.length };
        })
        .filter((item) => item.count > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [runs, runMeta, date, user?.uid],
  );

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <div className="text-sm text-gray-500">Loading your jobs...</div>
      </div>
    );
  }

  // When a job is opened, update assignments/{driverId_date}.currentJob
  const openJob = async (jobId, runLabel, targetCard = null, navState = null) => {
    if (!jobId || !user) return;

    // Make currentJob more informative for mixed-company days
    const meta = (runLabel && runMeta?.[runLabel]) || null;
    const companyShort = meta?.companyName ? ` - ${meta.companyName}` : "";
    const currentJobLabel = runLabel
      ? `${runLabel}${companyShort}`
      : String(jobId);

    try {
      const targetDocId = (runLabel && runDocMap?.[runLabel]) || assignmentDocId || `${user.uid}_${date}`;
      const assignmentRef = doc(db, "assignments", targetDocId);

      await setDoc(
        assignmentRef,
        {
          currentJob: currentJobLabel,
          currentJobUpdatedAt: serverTimestamp(),
        },
        { merge: true },
      );
    } catch (err) {
      console.error("Failed to update currentJob:", err);
    }

    const url = targetCard ? `/job/${jobId}?card=${targetCard}` : `/job/${jobId}`;
    navigate(url, navState ? { state: navState } : undefined);
  };

  // ---------- Break display helpers ----------
  const formatTime = (value) => {
    if (!value) return "";

    if (typeof value === "string") {
      const m = value.match(/^(\d{1,2}:\d{2})/);
      if (m) return m[1];

      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleTimeString("en-AU", {
          hour: "2-digit",
          minute: "2-digit",
        });
      }
      return "";
    }

    if (typeof value === "number") {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleTimeString("en-AU", {
          hour: "2-digit",
          minute: "2-digit",
        });
      }
    }

    return "";
  };

  const visibleBreaks = (breakHistory || []).filter((b) => {
    const s = formatTime(b.start);
    const e = formatTime(b.end);
    return s || e;
  });

  return (
    <div className="w-full px-4 py-4 sm:px-6 sm:py-5 xl:px-8 space-y-4 text-gray-900">
      <div className="flex items-baseline gap-2.5">
        <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-600">
          Driver
        </span>
        <h1 className="text-lg font-semibold tracking-tight text-gray-950">
          Today's jobs
        </h1>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-4 focus:ring-emerald-100"
        />
        <button
          onClick={handleBreakToggle}
          className={`px-3.5 py-2.5 rounded-xl font-medium text-white text-sm shadow-sm transition ${
            onBreak
              ? "bg-amber-500 hover:bg-amber-600"
              : "bg-emerald-600 hover:bg-emerald-700"
          }`}
        >
          {onBreak ? "End break" : "Start break"}
        </button>

        <div className="text-xs text-gray-500">
          <span className="font-semibold text-gray-900">{totalJobsCount}</span> jobs
          {visibleBreaks.length > 0 && (
            <>
              {" · Breaks: "}
              {visibleBreaks.map((b, idx) => {
                const startLabel = formatTime(b.start) || "";
                const endLabel = b.end ? formatTime(b.end) : "";
                return (
                  <span key={idx}>
                    {idx > 0 && " | "}
                    {startLabel}
                    {startLabel || endLabel ? " – " : ""}
                    {endLabel || (b.end == null && "…")}
                  </span>
                );
              })}
            </>
          )}
        </div>

        {runCompanySummary.length > 0 && (
          <div className="ml-auto flex flex-wrap gap-2">
            {runCompanySummary.map((r) => (
              <div
                key={r.label}
                className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 shadow-sm"
                title={r.uiVariant ? `Layout: ${r.uiVariant}` : ""}
              >
                <span className="text-[11px] font-semibold text-gray-800">
                  {r.label}
                </span>
                <span className="text-[11px] text-gray-500">
                  {r.companyName || "Company"}
                </span>
                <span className="text-[11px] font-medium text-gray-700">
                  {r.count}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Jobs or empty state */}
      {!assignment ? (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm px-4 py-8 text-center">
          <p className="text-sm text-gray-500">
            No jobs have been assigned for this day.
          </p>
        </div>
      ) : (
        <>
          {/* Job list */}
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-3 sm:p-4">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <h2 className="text-base sm:text-lg font-semibold text-gray-900">
                  Assigned jobs
                </h2>

                {/* New: Run -> Company summary (works for mixed-company assignments) */}
                {runCompanySummary.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-2">
                    {runCompanySummary.map((r) => (
                      <div
                        key={r.label}
                        className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1"
                        title={r.uiVariant ? `Layout: ${r.uiVariant}` : ""}
                      >
                        <span className="text-[11px] font-semibold text-gray-800">
                          {r.label}
                        </span>
                        <span className="text-[11px] text-gray-500">
                          {r.companyName || "Company"}
                        </span>
                        <span className="text-[11px] font-medium text-gray-700">
                          {r.count}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <span className="text-xs text-gray-500">
                {totalJobsCount} total jobs
              </span>
            </div>

            <DriverJobList
              assignment={assignment}
              date={date}
              user={user}
              handleTimestamp={handleTimestamp}
              refresh={refresh}
              onOpenJob={openJob}
              assignmentDocId={assignmentDocId}
              runDocMap={runDocMap}
            />
          </div>

          {/* Post trip card */}
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <button
              type="button"
              onClick={() => setPostTripSectionOpen((open) => !open)}
              aria-expanded={postTripSectionOpen}
              className="flex w-full items-center justify-between gap-3 p-3 text-left sm:p-4"
            >
              <span className="flex items-center gap-2">
                <span className="text-sm sm:text-base font-semibold text-gray-900">
                  Post trip
                </span>
                <span className="inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-800">
                  {runPostTripRequirements.length} required
                </span>
              </span>
              <svg
                className={`h-4 w-4 flex-shrink-0 text-gray-500 transition-transform ${postTripSectionOpen ? "rotate-180" : ""}`}
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>

            {postTripSectionOpen && (
            <div className="flex flex-col gap-3 px-3 pb-3 sm:px-4 sm:pb-4">
              <p className="text-xs sm:text-sm text-gray-500">
                  Complete one post-trip declaration for each truck/run you operate.
                </p>

              <div className="space-y-2">
                {runPostTripRequirements.map((req) => (
                  <div
                    key={req.label}
                    className="flex flex-col gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div>
                      <div className="text-sm font-semibold text-gray-900">
                        {req.label}
                      </div>
                      <div className="text-xs text-gray-500">
                        {req.vehicleLabel || req.companyName || "Truck details required"}
                      </div>
                    </div>
                    <button
                      onClick={async () => {
                        const snap = await getDoc(doc(db, "prestarts", req.prestartDocId));
                        if (!snap.exists() || !snap.data()?.completedAt) {
                          const proceed = window.confirm(
                            `Pre-start has not been completed for ${req.vehicleLabel || req.label}.\n\nOpen post-trip anyway?`,
                          );
                          if (!proceed) return;
                        }

                        setActivePostTripCtx(req);
                      }}
                      className="text-xs sm:text-sm bg-gray-900 text-white px-3 py-1.5 rounded-lg font-medium shadow-sm hover:bg-black transition"
                    >
                      Open post trip
                    </button>
                  </div>
                ))}
              </div>
            </div>
            )}
          </div>

          {/* Route map card */}
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <button
              type="button"
              onClick={() => setShowMapMobile((s) => !s)}
              aria-expanded={showMapMobile}
              className="flex w-full items-center justify-between gap-3 p-3 text-left sm:p-4"
            >
              <span className="text-sm sm:text-base font-semibold text-gray-900">
                Route map
              </span>
              <svg
                className={`h-4 w-4 flex-shrink-0 text-gray-500 transition-transform ${showMapMobile ? "rotate-180" : ""}`}
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>

            {showMapMobile && (
              <div className="mx-3 mb-3 rounded-lg overflow-hidden border border-gray-200 sm:mx-4 sm:mb-4">
                <DriverMap addresses={allAddresses} />
              </div>
            )}
          </div>
        </>
      )}

      {/* Post trip modal */}
      {activePostTripCtx && user && (
        <PostTripModal
          driverId={user.uid}
          date={date}
          driverEmail={user?.email || ""}
          postTripDocId={activePostTripCtx.postTripDocId}
          prestartDocId={activePostTripCtx.prestartDocId}
          runKey={activePostTripCtx.label}
          companyId={activePostTripCtx.companyId}
          companyName={activePostTripCtx.companyName}
          vehicleId={activePostTripCtx.vehicleId}
          vehicleRego={activePostTripCtx.vehicleRego}
          vehicleLoadType={activePostTripCtx.vehicleLoadType}
          vehicleLabel={activePostTripCtx.vehicleLabel}
          onClose={() => setActivePostTripCtx(null)}
          onCompleted={() => setActivePostTripCtx(null)}
        />
      )}
    </div>
  );
}
